import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message } from "@mimber/ui-web"
import {
  Form,
  InputField,
  InputNumberField,
  CurrencyInputField,
} from "@mimber/forms"
import {
  useAddInvoiceLineItem,
  useUpdateInvoiceLineItem,
} from "@mimber/client/mimber-organization"
import type { LineItemFormProps } from "./LineItemForm.types"
import {
  lineItemFormSchema,
  type LineItemFormData,
} from "./LineItemForm.schema"

export const LineItemForm: React.FunctionComponent<LineItemFormProps> = ({
  invoiceUuid,
  lineItem,
  onSuccess,
  onCancel,
}) => {
  const isEditing = !!lineItem

  const form = useForm<LineItemFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(lineItemFormSchema) as any,
    mode: "onBlur",
    defaultValues: {
      name: lineItem?.name ?? "",
      description: lineItem?.description ?? "",
      quantity: lineItem?.quantity ?? 1,
      unitAmount: lineItem?.unitAmount ?? 0,
    },
  })

  const { reset } = form

  const addMutation = useAddInvoiceLineItem(invoiceUuid, {
    onSuccess: () => {
      message.success("Line item added")
      reset()
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to add line item: ${error.message}`)
    },
  })

  const updateMutation = useUpdateInvoiceLineItem(
    invoiceUuid,
    lineItem?.uuid ?? "",
    {
      onSuccess: () => {
        message.success("Line item updated")
        onSuccess?.()
      },
      onError: (error) => {
        message.error(`Failed to update line item: ${error.message}`)
      },
    }
  )

  const mutation = isEditing ? updateMutation : addMutation

  const onSubmit = (data: LineItemFormData) => {
    mutation.mutate({
      name: data.name,
      description: data.description,
      quantity: data.quantity,
      unitAmount: data.unitAmount,
    })
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <InputField
        name="name"
        label="Name"
        placeholder="e.g., Annual Membership Fee"
        required
        maxLength={255}
      />

      <InputField
        name="description"
        label="Description (optional)"
        placeholder="Optional description"
        maxLength={1000}
      />

      <CurrencyInputField
        name="unitAmount"
        label="Unit Price"
        required
        minCents={0}
      />

      <InputNumberField name="quantity" label="Quantity" min={1} required />

      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={mutation.isPending}>
            Cancel
          </Button>
        )}
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          {isEditing ? "Save Changes" : "Add Line Item"}
        </Button>
      </Space>
    </Form>
  )
}
